import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, inr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { CheckCircle2, MapPin, Truck } from "lucide-react";

export default function Checkout() {
  const navigate = useNavigate();
  const { cart, refresh } = useCart();
  const [address, setAddress] = useState({
    full_name: "",
    phone: "",
    line1: "",
    city: "",
    pincode: "",
    landmark: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const subtotal = cart.subtotal || 0;
  const delivery = subtotal > 999 || subtotal === 0 ? 0 : 49;
  const total = subtotal + delivery;

  const setField = (k) => (e) => setAddress((a) => ({ ...a, [k]: e.target.value }));

  const placeOrder = async (e) => {
    e.preventDefault();
    if (!address.full_name || !address.phone || !address.line1 || !address.city || !address.pincode) {
      toast.error("Please fill all required fields"); return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders/checkout", { address });
      await refresh();
      toast.success("Order placed!");
      navigate(`/orders/${data.order_id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not place order");
    } finally { setSubmitting(false); }
  };

  if (!cart.items || cart.items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-20 text-center">
        <h1 className="font-display text-3xl font-bold">Your cart is empty</h1>
        <button className="vm-btn-primary mt-6" onClick={() => navigate("/shop")}>Continue shopping</button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10" data-testid="checkout-page">
      <p className="vm-overline">Checkout</p>
      <h1 className="font-display text-4xl md:text-5xl font-bold mt-2 tracking-tighter">
        Where should we deliver?
      </h1>

      <form onSubmit={placeOrder} className="grid lg:grid-cols-12 gap-8 mt-10">
        <div className="lg:col-span-8 space-y-5">
          <div className="vm-card p-6">
            <p className="vm-overline flex items-center gap-2"><MapPin className="h-3 w-3" /> Delivery address</p>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <div className="sm:col-span-2">
                <label className="text-xs text-neutral-400">Full name *</label>
                <input className="vm-input mt-1" value={address.full_name} onChange={setField("full_name")} data-testid="checkout-name" />
              </div>
              <div>
                <label className="text-xs text-neutral-400">Phone *</label>
                <input className="vm-input mt-1" value={address.phone} onChange={setField("phone")} data-testid="checkout-phone" />
              </div>
              <div>
                <label className="text-xs text-neutral-400">Pincode *</label>
                <input className="vm-input mt-1" value={address.pincode} onChange={setField("pincode")} data-testid="checkout-pincode" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-xs text-neutral-400">Address *</label>
                <input className="vm-input mt-1" value={address.line1} onChange={setField("line1")} placeholder="House no, street, area" data-testid="checkout-line1" />
              </div>
              <div>
                <label className="text-xs text-neutral-400">City *</label>
                <input className="vm-input mt-1" value={address.city} onChange={setField("city")} data-testid="checkout-city" />
              </div>
              <div>
                <label className="text-xs text-neutral-400">Landmark</label>
                <input className="vm-input mt-1" value={address.landmark} onChange={setField("landmark")} data-testid="checkout-landmark" />
              </div>
            </div>
          </div>

          <div className="vm-card p-6">
            <p className="vm-overline flex items-center gap-2"><Truck className="h-3 w-3" /> Delivery slot</p>
            <p className="text-sm text-neutral-300 mt-2">
              Standard quick delivery — your items arrive in <span className="text-volt-400 font-semibold">~30 minutes</span>.
            </p>
          </div>

          <div className="vm-card p-6">
            <p className="vm-overline">Payment</p>
            <div className="flex items-center gap-3 mt-3 p-3 rounded-xl border border-volt-500/40 bg-volt-500/5">
              <CheckCircle2 className="h-5 w-5 text-volt-400" />
              <div className="flex-1">
                <p className="text-sm font-semibold">Cash on Delivery</p>
                <p className="text-xs text-neutral-400">Pay our delivery agent on arrival.</p>
              </div>
              <span className="vm-chip vm-chip-primary">Selected</span>
            </div>
          </div>
        </div>

        <div className="lg:col-span-4">
          <div className="vm-card p-6 sticky top-24">
            <p className="vm-overline">Order summary</p>
            <div className="space-y-2 mt-4">
              {cart.items.map((it) => (
                <div key={it.product_id} className="flex justify-between text-sm">
                  <span className="text-neutral-300 truncate pr-2">{it.name} × {it.quantity}</span>
                  <span className="font-mono whitespace-nowrap">{inr(it.price * it.quantity)}</span>
                </div>
              ))}
            </div>
            <div className="vm-divider my-4" />
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-neutral-400">Subtotal</span><span className="font-mono">{inr(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-neutral-400">Delivery</span><span className="font-mono">{delivery === 0 ? <span className="text-green-400">FREE</span> : inr(delivery)}</span></div>
            </div>
            <div className="vm-divider my-4" />
            <div className="flex justify-between text-lg">
              <span className="font-semibold">Total</span>
              <span className="font-display font-bold">{inr(total)}</span>
            </div>
            <button type="submit" disabled={submitting} className="vm-btn-primary w-full mt-6" data-testid="checkout-place-order">
              {submitting ? "Placing..." : "Place order"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
